export const BOOKING_FUNNEL_VERSION = "booking_v2";

const EVENT_MARKER_PREFIX = "telefonka-posthog-booking-funnel-event-v2:";
const PAYMENT_MARKER_PREFIX = "telefonka-posthog-payment-success-v2:";

const BLOCKED_PROPERTY_KEYS = new Set([
  "name",
  "email",
  "phone",
  "address",
  "billing_address",
  "billing_name",
  "billing_country",
  "billing_postal_code",
  "billing_city",
  "billing_tax_number",
  "customer_name",
  "customer_email",
  "customer_phone",
  "event_location",
  "message",
  "note",
  "session_id",
  "stripe_session_id",
  "booking_id",
  "tax_number",
]);

let memoryRunId = null;
const memoryMarkers = new Set();

function safeSessionStorage() {
  try {
    return window.sessionStorage || null;
  } catch (_) {
    return null;
  }
}

function randomRunId() {
  return globalThis.crypto?.randomUUID?.()
    || `booking-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getOrCreateRunId() {
  if (!memoryRunId) memoryRunId = randomRunId();
  return memoryRunId;
}

function simpleHash(value) {
  let hash = 5381;
  const text = String(value || "");
  for (let index = 0; index < text.length; index += 1) {
    hash = ((hash << 5) + hash) ^ text.charCodeAt(index);
  }
  return (hash >>> 0).toString(36);
}

function memoryMarkerExists(marker) {
  return memoryMarkers.has(marker);
}

function rememberMemoryMarker(marker) {
  memoryMarkers.add(marker);
}

function persistentPaymentMarkerExists(marker) {
  if (memoryMarkers.has(marker)) return true;
  try {
    if (safeSessionStorage()?.getItem(marker) === "1") {
      memoryMarkers.add(marker);
      return true;
    }
  } catch (_) {
    // In-memory marker is enough when sessionStorage is unavailable.
  }
  return false;
}

function rememberPersistentPaymentMarker(marker) {
  memoryMarkers.add(marker);
  try {
    safeSessionStorage()?.setItem(marker, "1");
  } catch (_) {
    // Analytics storage must never affect booking/payment.
  }
}

function isSafeValue(value) {
  if (value === null) return true;
  if (["string", "number", "boolean"].includes(typeof value)) return true;
  return Array.isArray(value)
    && value.every((item) => item === null || ["string", "number", "boolean"].includes(typeof item));
}

export function sanitizeBookingAnalyticsProperties(properties = {}) {
  return Object.fromEntries(
    Object.entries(properties).filter(([key, value]) => (
      !BLOCKED_PROPERTY_KEYS.has(String(key).toLowerCase())
      && value !== undefined
      && isSafeValue(value)
    )),
  );
}

export function trackBookingEvent(name, properties = {}) {
  try {
    const capture = window.posthog?.capture;
    if (typeof capture !== "function") return false;
    capture.call(window.posthog, name, {
      ...sanitizeBookingAnalyticsProperties(properties),
      funnel_version: BOOKING_FUNNEL_VERSION,
    });
    return true;
  } catch (_) {
    // Analytics must never block booking or payment.
    return false;
  }
}

export function trackBookingEventOnce(name, properties = {}) {
  const runId = getOrCreateRunId();
  const marker = `${EVENT_MARKER_PREFIX}${runId}:${name}`;
  if (memoryMarkerExists(marker)) return false;
  const tracked = trackBookingEvent(name, properties);
  if (tracked) rememberMemoryMarker(marker);
  return tracked;
}

export function trackPaymentSuccessOnce(sessionId, properties = {}) {
  if (!sessionId) return false;
  const marker = `${PAYMENT_MARKER_PREFIX}${simpleHash(sessionId)}`;
  if (persistentPaymentMarkerExists(marker)) return false;
  const tracked = trackBookingEvent("payment_success", properties);
  if (tracked) {
    rememberPersistentPaymentMarker(marker);
    completeBookingFunnelRun();
  }
  return tracked;
}

export function completeBookingFunnelRun() {
  memoryRunId = null;
}

export function resetBookingAnalyticsMemoryForTests() {
  memoryRunId = null;
  memoryMarkers.clear();
}

export function resetBookingAnalyticsForTests() {
  resetBookingAnalyticsMemoryForTests();
  try {
    const storage = safeSessionStorage();
    if (!storage) return;
    const keys = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key?.startsWith(PAYMENT_MARKER_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => storage.removeItem(key));
  } catch (_) {
    // Test reset is best effort for non-browser environments.
  }
}
